import React, { useState, useEffect, useRef } from 'react';
import { Store, Truck, CreditCard, Bell, Shield, Camera, Save, Plus, Navigation, Plane, ShieldCheck, Mail, Lock, LogOut, CheckCircle, Power, Clock, Trophy, Zap, MessageSquare, RefreshCw, User } from 'lucide-react';
import { getSellerProfile, updateSellerProfile } from '../services/api';
import { uploadToCloudinary } from '../services/cloudinary';

type TabType = 'profile' | 'delivery' | 'subscription' | 'notifications' | 'account';

interface SellerProfile {
  seller_id?: string;
  business_name: string;
  contact_phone: string;
  city: string;
  about_shop: string;
  logo_url?: string;
  created_at?: string;
  verification_status?: string;
}

const Settings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [profile, setProfile] = useState<SellerProfile>({
    business_name: '',
    contact_phone: '',
    city: '',
    about_shop: '',
    logo_url: ''
  });
  
  // FIX 12 (Aug 12, 2026): added `isError` so a genuine save failure can be
  // shown honestly instead of the UI staying silent (previously the only
  // failure path was updateSellerProfile's now-removed fake-success
  // fallback, so no error state existed here at all). Default 'success'
  // keeps every other existing showToast(...) call-site untouched.
  const [toast, setToast] = useState<{message: string, visible: boolean, isError?: boolean}>({ message: '', visible: false });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load active tab from hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as TabType;
    if (['profile', 'delivery', 'subscription', 'notifications', 'account'].includes(hash)) {
      setActiveTab(hash);
    } else {
      window.location.hash = 'profile';
    }
  }, []);

  const changeTab = (tab: TabType) => {
    setActiveTab(tab);
    window.location.hash = tab;
  };

  const showToast = (message: string, isError: boolean = false) => {
    setToast({ message, visible: true, isError });
    setTimeout(() => setToast({ message: '', visible: false }), 3000);
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await getSellerProfile();
      // Only set what exists to not override local defaults initially, but populate if exists
      setProfile(prev => ({
        ...prev,
        ...data,
        about_shop: data.about_shop || localStorage.getItem('seller_about_shop') || '',
        logo_url: data.logo_url || localStorage.getItem('seller_logo_url') || ''
      }));
    } catch (error) {
      console.error('Error fetching profile', error);
      // Fallback
      setProfile(prev => ({
        ...prev,
        about_shop: localStorage.getItem('seller_about_shop') || '',
        logo_url: localStorage.getItem('seller_logo_url') || ''
      }));
    } finally {
      setLoading(false);
    }
  };

  // --- TAB 1: SHOP PROFILE ---
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      const dataToSave = {
        business_name: profile.business_name,
        contact_phone: profile.contact_phone,
        city: profile.city
      };
      await updateSellerProfile(dataToSave);

      // Update local state to reflect changes instantly as instructed
      setProfile(prev => ({ ...prev, ...dataToSave }));

      localStorage.setItem('seller_about_shop', profile.about_shop);
      showToast('Profile updated!');
    } catch (error: any) {
      console.error('Error saving profile', error);
      // FIX 12 (Aug 12, 2026): previously this catch block was silent —
      // updateSellerProfile could never actually reject before (it faked
      // success on 404), so there was no failure UI to build. Now that the
      // real endpoint exists and can genuinely return an error (validation,
      // network, etc.), the user must be told the save did NOT happen,
      // instead of the button just stopping its spinner with no feedback.
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Could not save profile. Please try again.';
      showToast(message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadingLogo(true);
      const file = e.target.files[0];
      try {
        const url = await uploadToCloudinary(file, undefined, 'readora/seller-logos');
        setProfile(prev => ({ ...prev, logo_url: url }));
        localStorage.setItem('seller_logo_url', url);
        showToast('Logo uploaded!');
      } catch (error) {
        console.error('Upload failed', error);
      } finally {
        setUploadingLogo(false);
      }
    }
  };

  // --- TAB 2: DELIVERY SETTINGS ---
  const [deliveryType, setDeliveryType] = useState(localStorage.getItem('deliveryType') || 'Both');
  const [allowPickup, setAllowPickup] = useState(localStorage.getItem('allowPickup') !== 'false');
  const [vacationMode, setVacationMode] = useState(localStorage.getItem('vacationMode') === 'true');
  const [vacationMessage, setVacationMessage] = useState(localStorage.getItem('vacationMessage') || "I'm on vacation. Back soon.");
  const [paymentMethods, setPaymentMethods] = useState({
    cod: localStorage.getItem('pay_cod') !== 'false',
    bank: localStorage.getItem('pay_bank') !== 'false',
    online: localStorage.getItem('pay_online') === 'true'
  });
  const [zones, setZones] = useState(() => {
    const saved = localStorage.getItem('deliveryZones');
    return saved ? JSON.parse(saved) : [
      { id: '1', city: 'Colombo', fee: 150, active: true },
      { id: '2', city: 'Kandy', fee: 250, active: true },
      { id: '3', city: 'Galle', fee: 300, active: true },
      { id: '4', city: 'Outstation', fee: 350, active: true },
    ];
  });

  const [newCityName, setNewCityName] = useState('');
  const [newCityFee, setNewCityFee] = useState('');

  const addZone = () => {
    if (newCityName && newCityFee) {
      setZones([...zones, { id: Date.now().toString(), city: newCityName, fee: Number(newCityFee), active: true }]);
      setNewCityName('');
      setNewCityFee('');
    }
  };

  const toggleZone = (id: string) => {
    setZones(zones.map((z: any) => z.id === id ? { ...z, active: !z.active } : z));
  };

  const updateZoneFee = (id: string, fee: number) => {
    setZones(zones.map((z: any) => z.id === id ? { ...z, fee } : z));
  };

  const saveDeliverySettings = () => {
    setLoading(true);
    setTimeout(() => {
      localStorage.setItem('deliveryType', deliveryType);
      localStorage.setItem('allowPickup', allowPickup.toString());
      localStorage.setItem('vacationMode', vacationMode.toString());
      localStorage.setItem('vacationMessage', vacationMessage);
      localStorage.setItem('pay_cod', paymentMethods.cod.toString());
      localStorage.setItem('pay_bank', paymentMethods.bank.toString());
      localStorage.setItem('pay_online', paymentMethods.online.toString());
      localStorage.setItem('deliveryZones', JSON.stringify(zones));
      showToast('Settings saved!');
      setLoading(false);
    }, 500);
  };

  // --- TAB 3: SUBSCRIPTION ---
  const calculateDaysRemaining = () => {
    if (!profile.created_at) return 90;
    const created = new Date(profile.created_at);
    const msPerDay = 1000 * 60 * 60 * 24;
    const now = new Date();
    const daysElapsed = Math.floor((now.getTime() - created.getTime()) / msPerDay);
    return Math.max(0, 90 - daysElapsed);
  };
  const daysRemaining = calculateDaysRemaining();
  const trialProgress = ((90 - daysRemaining) / 90) * 100;

  // --- TAB 4: NOTIFICATIONS ---
  const [notifs, setNotifs] = useState(() => {
    const saved = localStorage.getItem('seller_notifs');
    return saved ? JSON.parse(saved) : {
      emailOrder: true, emailStatus: true, emailReview: true, emailLowStock: true, emailWeekly: false,
      pushMessage: true, pushFlash: false
    };
  });

  const toggleNotif = (key: string) => {
    setNotifs((prev: any) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveNotifications = () => {
    setLoading(true);
    setTimeout(() => {
      localStorage.setItem('seller_notifs', JSON.stringify(notifs));
      showToast('Settings saved!');
      setLoading(false);
    }, 500);
  };

  // --- TAB 5: ACCOUNT ---
  const handlePasswordUpdate = () => {
    setLoading(true);
    setTimeout(() => {
      showToast('Password updated!');
      setLoading(false);
    }, 800);
  };

  const handleDeactivate = () => {
    if (window.confirm('Are you sure you want to deactivate your account? This will hide all your listings and cannot be undone immediately.')) {
      showToast('Account deactivated.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F1EFE8] pb-20">
      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed top-24 right-8 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-3 z-50 animate-in fade-in slide-in-from-top-5 ${toast.isError ? 'bg-[#D85A30]' : 'bg-[#1D9E75]'}`}>
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Header Area */}
      <div className="bg-[#085041] pt-12 pb-24 px-8 rounded-b-[40px] shadow-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Shop Settings</h1>
            <p className="text-[#F1EFE8]/80">Manage your business preferences and configurations.</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-8 -mt-14 flex flex-col md:flex-row gap-8">
        
        {/* Sidebar Nav */}
        <div className="md:w-64 shrink-0 flex flex-col gap-2">
          {[
            { id: 'profile', icon: Store, label: 'Shop Profile' },
            { id: 'delivery', icon: Truck, label: 'Delivery' },
            { id: 'subscription', icon: CreditCard, label: 'Subscription' },
            { id: 'notifications', icon: Bell, label: 'Notifications' },
            { id: 'account', icon: Shield, label: 'Account' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => changeTab(tab.id as TabType)}
              className={`flex items-center gap-3 px-5 py-4 rounded-2xl font-medium transition-all duration-300 ${
                activeTab === tab.id 
                  ? 'bg-white text-[#085041] shadow-sm scale-105 transform origin-left' 
                  : 'bg-transparent text-[#085041]/60 hover:bg-white/50'
              }`}
            >
              <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'stroke-[2.5px]' : ''}`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content Cards */}
        <div className="flex-1 space-y-6">
          
          {/* ========================================================= */}
          {/* TAB 1: SHOP PROFILE */}
          {/* ========================================================= */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-[32px] p-8 shadow-sm">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-[#085041] mb-1">Shop Profile</h2>
                  <p className="text-gray-500">Public details displayed to buyers.</p>
                </div>
                {/* Logo Upload */}
                <div className="flex flex-col items-center gap-2">
                  <div 
                    className="w-24 h-24 rounded-full bg-[#F1EFE8] border border-gray-200 overflow-hidden relative group cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {profile.logo_url ? (
                      <img src={profile.logo_url} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                        <Store className="w-8 h-8 opacity-50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      {uploadingLogo ? (
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Camera className="w-6 h-6 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-[#085041] bg-[#085041]/10 px-3 py-1 rounded-full cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </span>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleLogoUpload} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-1.5 focus-within:text-[#085041] text-gray-700">
                  <label className="text-sm font-semibold uppercase tracking-wider ml-1">Business Name</label>
                  <input 
                    type="text" name="business_name" value={profile.business_name} onChange={handleProfileChange}
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 text-lg font-medium focus:ring-2 focus:ring-[#085041]"
                  />
                </div>
                <div className="space-y-1.5 focus-within:text-[#085041] text-gray-700">
                  <label className="text-sm font-semibold uppercase tracking-wider ml-1">Contact Phone</label>
                  <input 
                    type="text" name="contact_phone" value={profile.contact_phone || ''} onChange={handleProfileChange}
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 text-lg font-medium focus:ring-2 focus:ring-[#085041]"
                  />
                </div>
                <div className="space-y-1.5 focus-within:text-[#085041] text-gray-700 col-span-1 md:col-span-2">
                  <label className="text-sm font-semibold uppercase tracking-wider ml-1">City</label>
                  <input 
                    type="text" name="city" value={profile.city || ''} onChange={handleProfileChange}
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 text-lg font-medium focus:ring-2 focus:ring-[#085041]"
                  />
                </div>
                <div className="space-y-1.5 focus-within:text-[#085041] text-gray-700 col-span-1 md:col-span-2">
                  <label className="text-sm font-semibold uppercase tracking-wider ml-1">About Shop</label>
                  <textarea 
                    name="about_shop" value={profile.about_shop} onChange={handleProfileChange} rows={4}
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 text-lg font-medium focus:ring-2 focus:ring-[#085041] resize-none"
                    placeholder="Tell buyers what makes your book collection special..."
                  />
                </div>
              </div>

              {/* Trust Badges Region */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider ml-1 mb-3">Trust Badges</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex flex-col items-center text-center gap-1">
                    <Trophy className="w-6 h-6 text-amber-500" />
                    <span className="text-xs font-bold text-amber-700">Top Seller</span>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex flex-col items-center text-center gap-1">
                    <Zap className="w-6 h-6 text-blue-500" />
                    <span className="text-xs font-bold text-blue-700">Fast Shipper</span>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex flex-col items-center text-center gap-1 opacity-50 grayscale">
                    <MessageSquare className="w-6 h-6 text-gray-500" />
                    <span className="text-xs font-bold text-gray-600">Quick Replies</span>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex flex-col items-center text-center gap-1">
                    <RefreshCw className="w-6 h-6 text-emerald-500" />
                    <span className="text-xs font-bold text-emerald-700">100% Completion</span>
                  </div>
                </div>
              </div>

              {/* Verification Status */}
              <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-2xl mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-amber-900">Pending Verification</h4>
                    <p className="text-sm text-amber-700">Submit NIC for verified badge</p>
                  </div>
                </div>
                <button className="px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 font-bold rounded-full text-sm transition-colors">
                  Submit Now
                </button>
              </div>

              <div className="flex justify-end">
                <button 
                  onClick={saveProfile} disabled={loading}
                  className="bg-[#085041] hover:bg-teal-900 text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 transition-transform active:scale-95 disabled:opacity-70"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 2: DELIVERY SETTINGS */}
          {/* ========================================================= */}
          {activeTab === 'delivery' && (
            <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-8 animate-in fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#085041] mb-1">Delivery & Logistics</h2>
                <p className="text-gray-500">Configure how books reach your buyers.</p>
              </div>

              {/* Delivery Methods */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 ml-1">Delivery Method</h3>
                <div className="flex gap-4">
                  {['Self Delivery', 'Courier', 'Both'].map(opt => (
                    <label key={opt} className={`flex-1 flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${deliveryType === opt ? 'border-[#085041] bg-[#085041]/5' : 'border-[#F1EFE8] hover:border-gray-200'}`}>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deliveryType === opt ? 'border-[#085041]' : 'border-gray-300'}`}>
                        {deliveryType === opt && <div className="w-2.5 h-2.5 bg-[#085041] rounded-full" />}
                      </div>
                      <span className="font-bold text-gray-800">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Pickup Option */}
              <div className="flex items-center justify-between p-4 bg-[#F1EFE8]/50 rounded-2xl">
                <div>
                  <h4 className="font-bold text-[#085041]">Pickup Option</h4>
                  <p className="text-sm text-gray-500">Allow buyers to pick up from your city.</p>
                </div>
                <button 
                  onClick={() => setAllowPickup(!allowPickup)}
                  className={`w-14 h-8 rounded-full p-1 transition-colors ${allowPickup ? 'bg-[#1D9E75]' : 'bg-gray-300'}`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full shadow-sm transform transition-transform ${allowPickup ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>

              {/* Delivery Zones */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="bg-[#F1EFE8]/50 px-5 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h4 className="font-bold text-[#085041]">Delivery Zones & Fees</h4>
                </div>
                <div className="divide-y divide-gray-100">
                  {zones.map((z: any) => (
                    <div key={z.id} className={`flex items-center px-5 py-3 transition-opacity ${z.active ? 'opacity-100' : 'opacity-40 bg-gray-50'}`}>
                      <div className="flex-1 font-semibold text-gray-800">{z.city}</div>
                      <div className="w-32 mr-4 relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">Rs.</span>
                        <input 
                          type="number" value={z.fee} onChange={(e) => updateZoneFee(z.id, Number(e.target.value))}
                          disabled={!z.active}
                          className="w-full bg-[#F1EFE8] border-none rounded-lg pl-9 pr-3 py-2 font-bold text-gray-700 disabled:opacity-50 text-right"
                        />
                      </div>
                      <button onClick={() => toggleZone(z.id)} className={`w-10 h-6 rounded-full p-0.5 transition-colors ${z.active ? 'bg-[#1D9E75]' : 'bg-gray-300'}`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform ${z.active ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add New Zone */}
                  <div className="flex items-center px-5 py-3 bg-gray-50">
                    <input 
                      type="text" placeholder="Add City..." value={newCityName} onChange={e => setNewCityName(e.target.value)}
                      className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 font-medium text-gray-700 mr-4"
                    />
                    <div className="w-32 mr-4 relative">
                      <input 
                        type="number" placeholder="Fee" value={newCityFee} onChange={e => setNewCityFee(e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-lg pl-3 pr-3 py-2 font-medium text-gray-700"
                      />
                    </div>
                    <button onClick={addZone} className="p-2 bg-[#085041] text-white rounded-lg hover:bg-teal-900 transition-colors">
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 ml-1">Payment Methods</h3>
                <div className="flex gap-4">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={paymentMethods.cod} onChange={e => setPaymentMethods(prev => ({...prev, cod: e.target.checked}))} className="w-5 h-5 text-[#085041] rounded focus:ring-[#085041]" />
                    <span className="font-medium text-gray-700">Cash on Delivery</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={paymentMethods.bank} onChange={e => setPaymentMethods(prev => ({...prev, bank: e.target.checked}))} className="w-5 h-5 text-[#085041] rounded focus:ring-[#085041]" />
                    <span className="font-medium text-gray-700">Bank Transfer</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={paymentMethods.online} onChange={e => setPaymentMethods(prev => ({...prev, online: e.target.checked}))} className="w-5 h-5 text-[#085041] rounded focus:ring-[#085041]" />
                    <span className="font-medium text-gray-700">Online Payment</span>
                  </label>
                </div>
              </div>

              {/* Vacation Mode */}
              <div className={`border-2 rounded-2xl p-5 transition-colors ${vacationMode ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl flex items-center justify-center ${vacationMode ? 'bg-amber-100' : 'bg-gray-100'}`}>
                      <Plane className={`w-6 h-6 ${vacationMode ? 'text-amber-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-lg ${vacationMode ? 'text-amber-900' : 'text-gray-800'}`}>🏖️ Vacation Mode</h4>
                      <p className={`text-sm ${vacationMode ? 'text-amber-700' : 'text-gray-500'}`}>Pause all listings temporarily.</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setVacationMode(!vacationMode)}
                    className={`w-14 h-8 rounded-full p-1 transition-colors ${vacationMode ? 'bg-amber-500' : 'bg-gray-300'}`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow-sm transform transition-transform ${vacationMode ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
                </div>
                
                {vacationMode && (
                  <div className="animate-in slide-in-from-top-2 pt-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-amber-800 ml-1 mb-1 block">Auto-Reply Message</label>
                    <input 
                      type="text" value={vacationMessage} onChange={e => setVacationMessage(e.target.value)}
                      className="w-full bg-white border border-amber-200 rounded-xl px-4 py-3 font-medium text-amber-900 focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <button 
                  onClick={saveDeliverySettings} disabled={loading}
                  className="bg-[#085041] hover:bg-teal-900 text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 transition-transform active:scale-95 disabled:opacity-70"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
                  Save Delivery Settings
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 3: SUBSCRIPTION */}
          {/* ========================================================= */}
          {activeTab === 'subscription' && (
            <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-8 animate-in fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#085041] mb-1">Current Plan</h2>
                <p className="text-gray-500">Manage your Readora seller subscription.</p>
              </div>

              {daysRemaining <= 0 ? (
                <div className="bg-[#D85A30]/10 border border-[#D85A30]/30 p-6 rounded-2xl relative overflow-hidden">
                  <h3 className="text-xl font-bold text-[#D85A30] mb-2">Trial Ended</h3>
                  <p className="text-[#D85A30]/80 font-medium font-medium mb-4">Please subscribe to continue selling.</p>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-[#085041] to-teal-800 p-8 rounded-3xl relative overflow-hidden text-white shadow-md">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <Store className="w-48 h-48 transform rotate-12 translate-x-12 -translate-y-12" />
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-1 relative z-10 flex items-center gap-3">
                    🆓 Free Trial 
                    <span className="text-sm font-semibold bg-white/20 px-3 py-1 rounded-full">{daysRemaining} Days Left</span>
                  </h3>
                  <p className="text-[#F1EFE8]/80 mb-8 relative z-10">Enjoy unrestricted selling during your trial period.</p>
                  
                  <div className="relative z-10 mb-2 flex justify-between text-sm font-bold">
                    <span>Day {90 - daysRemaining}</span>
                    <span>Day 90</span>
                  </div>
                  <div className="h-3 bg-black/20 rounded-full relative z-10 overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{ width: `${trialProgress}%` }} />
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-bold text-[#085041] mb-4">Plans after trial</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  
                  {/* Plan 1 */}
                  <div className="border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-shadow bg-white flex flex-col">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold uppercase tracking-wider mb-3">Casual Seller</span>
                      <h4 className="text-2xl font-bold text-gray-900">Personal</h4>
                      <div className="flex items-baseline gap-1 mt-2 mb-2">
                        <span className="text-sm font-bold text-gray-500">LKR</span>
                        <span className="text-4xl font-bold text-[#085041]">299</span>
                        <span className="text-gray-500">/mo</span>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-8 flex-1">
                      {['50 active listings', 'Basic shop stats', 'Standard email support', 'Readora fee: 5%'].map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-600 font-medium">
                          <CheckCircle className="w-5 h-5 text-[#1D9E75]" /> {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <button className="w-full py-3 rounded-xl border-2 border-[#085041] text-[#085041] font-bold hover:bg-[#085041]/5 transition-colors">
                      Choose Plan
                    </button>
                  </div>

                  {/* Plan 2 */}
                  <div className="border-2 border-[#085041] rounded-2xl p-6 shadow-md bg-white flex flex-col relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-[#085041] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      RECOMMENDED
                    </div>
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold uppercase tracking-wider mb-3">Pro Seller</span>
                      <h4 className="text-2xl font-bold text-gray-900">Business</h4>
                      <div className="flex items-baseline gap-1 mt-2 mb-2">
                        <span className="text-sm font-bold text-gray-500">LKR</span>
                        <span className="text-4xl font-bold text-[#085041]">999</span>
                        <span className="text-gray-500">/mo</span>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-8 flex-1">
                      {['Unlimited listings', 'Full analytics & reports', 'Priority chat support', 'Top Seller badge eligible', 'Readora fee: 2%'].map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-800 font-bold">
                          <CheckCircle className="w-5 h-5 text-[#085041]" /> {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <button className="w-full py-3 rounded-xl bg-[#085041] text-white font-bold hover:bg-teal-900 transition-colors shadow-sm relative z-10">
                      Choose Plan
                    </button>
                  </div>

                </div>
              </div>

              <div className="flex items-center justify-center p-4 bg-[#F1EFE8]/50 rounded-2xl text-gray-500 font-medium">
                <CreditCard className="w-5 h-5 mr-2" /> Payment integration via PayHere coming soon
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 4: NOTIFICATIONS */}
          {/* ========================================================= */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-8 animate-in fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#085041] mb-1">Notifications</h2>
                <p className="text-gray-500">Choose how and when we contact you.</p>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="flex items-center gap-2 font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                    <Mail className="w-5 h-5 text-[#085041]" /> Email Notifications
                  </h3>
                  <div className="space-y-4">
                    {[
                      { key: 'emailOrder', label: 'New order received', desc: 'Instantly get notified when someone buys your book.' },
                      { key: 'emailStatus', label: 'Order status update', desc: 'Alerts when buyer marks order as delivered.' },
                      { key: 'emailReview', label: 'New review received', desc: 'Get notified when a buyer leaves feedback.' },
                      { key: 'emailLowStock', label: 'Low stock alert', desc: 'When your book stock drops to 1.' },
                      { key: 'emailWeekly', label: 'Weekly report', desc: 'A Sunday summary of your shop performance.' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-800">{item.label}</p>
                          <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <button 
                          onClick={() => toggleNotif(item.key)}
                          className={`w-12 h-6 rounded-full p-1 transition-colors ${notifs[item.key] ? 'bg-[#1D9E75]' : 'bg-gray-300'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${notifs[item.key] ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4">
                  <h3 className="flex items-center gap-2 font-bold text-gray-800 mb-4 pb-2 border-b border-gray-100">
                    <Bell className="w-5 h-5 text-[#085041]" /> Push Notifications
                  </h3>
                  <div className="space-y-4">
                    {[
                      { key: 'pushMessage', label: 'New message', desc: 'Direct messages from buyers.' },
                      { key: 'pushFlash', label: 'Flash deals nearby', desc: 'Alerts you about big promotional events.' }
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-gray-800">{item.label}</p>
                          <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <button 
                          onClick={() => toggleNotif(item.key)}
                          className={`w-12 h-6 rounded-full p-1 transition-colors ${notifs[item.key] ? 'bg-[#1D9E75]' : 'bg-gray-300'}`}
                        >
                          <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${notifs[item.key] ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button 
                  onClick={saveNotifications} disabled={loading}
                  className="bg-[#085041] hover:bg-teal-900 text-white px-8 py-3.5 rounded-xl font-bold flex items-center gap-2 transition-transform active:scale-95 disabled:opacity-70"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="w-5 h-5" />}
                  Save Preferences
                </button>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 5: ACCOUNT */}
          {/* ========================================================= */}
          {activeTab === 'account' && (
            <div className="bg-white rounded-[32px] p-8 shadow-sm space-y-8 animate-in fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#085041] mb-1">Account & Security</h2>
                <p className="text-gray-500">Manage your private credentials.</p>
              </div>

              <div className="bg-[#F1EFE8]/50 p-6 rounded-2xl flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-white shadow-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-10 h-10 text-[#085041]" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-gray-800">Vishwa Kelum</h3>
                  <p className="text-gray-600 font-medium">vishwa2@test.com</p>
                  <p className="text-sm text-gray-400 mt-2">Member since March 2026</p>
                </div>
              </div>

              {/* Password */}
              <div className="border border-gray-200 p-6 rounded-2xl">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-[#085041]" /> Change Password
                </h3>
                <div className="space-y-4 max-w-md">
                  <input 
                    type="password" placeholder="Current Password"
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 font-medium focus:ring-2 focus:ring-[#085041]"
                  />
                  <input 
                    type="password" placeholder="New Password"
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 font-medium focus:ring-2 focus:ring-[#085041]"
                  />
                  <input 
                    type="password" placeholder="Confirm Password"
                    className="w-full bg-[#F1EFE8] border-none rounded-xl px-4 py-3 font-medium focus:ring-2 focus:ring-[#085041]"
                  />
                  <button 
                    onClick={handlePasswordUpdate} disabled={loading}
                    className="bg-gray-800 hover:bg-black text-white px-6 py-3 rounded-xl font-bold transition-colors disabled:opacity-70 mt-2"
                  >
                    Update Password
                  </button>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="border-2 border-[#D85A30]/20 p-6 rounded-2xl bg-[#D85A30]/5">
                <h3 className="text-lg font-bold text-[#D85A30] mb-2">Danger Zone</h3>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-gray-800">Deactivate Account</h4>
                    <p className="text-sm text-gray-600">This will hide all your listings from buyers.</p>
                  </div>
                  <button 
                    onClick={handleDeactivate}
                    className="bg-[#D85A30] hover:bg-[#b54522] text-white px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-sm transition-colors whitespace-nowrap"
                  >
                    ⚠️ Deactivate
                  </button>
                </div>
              </div>

              {/* Logout */}
              <button className="flex items-center gap-2 text-gray-500 font-bold hover:text-gray-800 transition-colors pt-4 mx-auto md:mx-0">
                <LogOut className="w-5 h-5" /> Logout from all devices
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Settings;
